import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import '../styles/AboutUs.css';
import ScrollProgressIndicator from '../components/ScrollProgressIndicator';

const AboutUs = () => {
  const [flippedCards, setFlippedCards] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [hoveredStat, setHoveredStat] = useState(null);
  const [hoveredValue, setHoveredValue] = useState(null);
  const [currentStatIndex, setCurrentStatIndex] = useState(0);
  const [hoveredTimeline, setHoveredTimeline] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const video = document.getElementById('abtu-myVideo');
    if (video) {
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.muted = true;
      video.autoplay = true;
      
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('About video autoplay started');
          })
          .catch((error) => {
            console.log('About video autoplay prevented:', error);
          });
      }
    }
  }, []);

  useEffect(() => {
    if (isMobile) {
      const interval = setInterval(() => {
        setCurrentStatIndex((prevIndex) => 
          (prevIndex + 1) % 4
        );
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [isMobile]);

  const toggleCard = (cardId) => {
    setFlippedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };


  const stats = [
    { number: '99.1%', label: 'Purity Level', icon: '⚗️', color: '#00ff41' },
    { number: '75+', label: 'Cooked Projects', icon: '🔥', color: '#ff6b35' },
    { number: '2+', label: 'Years in Business', icon: '⏰', color: '#ffd23f' },
    { number: '24/7', label: 'Lab Operations', icon: '🏭', color: '#14eaf5' }
  ];

  const values = [
    {
      title: 'Precision',
      description: 'Every line of code is measured with scientific precision. No contamination, no shortcuts.',
      icon: '🎯',
      gradient: 'linear-gradient(135deg, #00ff41, #32cd32)'
    },
    {
      title: 'Chemistry',
      description: 'The perfect blend of technology and creativity creates explosive digital solutions.',
      icon: '⚗️',
      gradient: 'linear-gradient(135deg, #ff6b35, #ff8c42)'
    },
    {
      title: 'Empire',
      description: 'Building digital empires one project at a time. We are in the empire business.',
      icon: '👑',
      gradient: 'linear-gradient(135deg, #ffd23f, #fccc63)'
    },
    {
      title: 'Results',
      description: 'We deliver results that are 99.1% pure satisfaction. No half measures.',
      icon: '💎',
      gradient: 'linear-gradient(135deg, #14eaf5, #00adb5)'
    }
  ];

  const timeline = [
    {
      year: '2023',
      title: 'The Genesis',
      description: 'QuantumFrame Studio was founded with a vision to revolutionize digital solutions.',
      icon: '🚀',
      color: '#00ff41'
    },
    {
      year: '2024',
      title: 'Expansion',
      description: 'Grew our team and diversified into multimedia production and social media management.',
      icon: '📈',
      color: '#ff6b35'
    },
    {
      year: '2025',
      title: 'Empire Building',
      description: 'Established ourselves as a full-service digital agency with 75+ successful projects.',
      icon: '👑',
      color: '#ffd23f'
    }
  ];

  const services = [
    {
      title: 'Web Development',
      description: 'Custom websites and web applications built with cutting-edge technology',
      icon: '💻',
      color: '#00ff41'
    },
    {
      title: 'Video Production',
      description: 'Professional filmmaking and editing services that tell your story',
      icon: '🎬',
      color: '#ff6b35'
    },
    {
      title: 'Social Media',
      description: 'Strategic social media management to grow your digital presence',
      icon: '📱',
      color: '#14eaf5'
    },
    {
      title: 'Operations',
      description: 'Seamless project management and operational excellence',
      icon: '⚙️',
      color: '#ffd23f'
    }
  ];

  const team = [
    {
      id: 'founder',
      name: 'Aydin Khan',
      role: 'Chief Architect & Operations Director',
      characterImg: '/assets/images/Walter-white.png',
      realImg: '/assets/images/Aydin-khan.png',
      tags: ['Full-Stack Alchemy', 'Digital Chemistry', 'Operations & Strategy', 'Team Leadership', 'Empire Building', '99.1% Pure Code'],
      description: 'Visionary leader orchestrating digital excellence at every level. Master of full-stack development who handles everything from strategic code architecture to daily operations and team guidance. Turning raw ideas into pure, crystallized innovation with surgical precision. The mastermind architecting our blue-sky empire while ensuring operational excellence across all fronts.',
      email: 'aydin@quantumframestudio.com',
      quote: '"I architect the code, orchestrate the operations, and guide the vision!"',
      indicator: { text: '⚠️ DANGER', color: 'rgba(255, 107, 53, 0.9)' },
      quoteColor: '#00ff41',
      buttonGradient: 'linear-gradient(135deg, #00ff41, #32cd32)',
      buttonText: 'Enter My Domain'
    },
    {
      id: 'operations',
      name: 'Nauman Zafar',
      role: 'The Saul of Operations',
      characterImg: '/assets/images/Saul.webp',
      realImg: '/assets/images/Nauman.png',
      tags: ['Strategic Planning', 'Client Relations', 'Project Management', 'Better Call Nauman'],
      description: 'The smooth operator who keeps everything running like clockwork. With exceptional organizational skills and a talent for problem-solving, he ensures every project is delivered on time and exceeds expectations. When things get complicated, better call Nauman.',
      email: 'nauman@quantumframestudio.com',
      quote: '"It\'s all good, man!"',
      indicator: { text: '⚖️ LEGAL', color: 'rgba(255, 210, 63, 0.9)' },
      quoteColor: '#ffd23f',
      buttonGradient: 'linear-gradient(135deg, #ffd23f, #fccc63)',
      buttonText: 'Better Call Nauman'
    },
    {
      id: 'filmmaker',
      name: 'Sheikh Uneeb',
      role: 'The Gus of Visual Storytelling',
      characterImg: '/assets/images/Gustavo.jpg',
      realImg: '/assets/images/Uneeb.png',
      tags: ['Filmmaking', 'Video Editing', 'Visual Effects', 'Cinematic Excellence'],
      description: 'A perfectionist with an eye for detail that would make Gustavo Fring proud. From concept to final cut, he crafts visual narratives that captivate and engage. His meticulous approach to filmmaking ensures every frame is a work of art.',
      email: 'uneeb@quantumframestudio.com',
      quote: '"I hide in plain sight."',
      indicator: { text: '🎬 DIRECTOR', color: 'rgba(20, 234, 245, 0.9)' },
      quoteColor: '#14eaf5',
      buttonGradient: 'linear-gradient(135deg, #14eaf5, #00adb5)',
      buttonText: 'See The Vision'
    },
    {
      id: 'social',
      name: 'Nabeel Khan',
      role: 'The Pinkman of Social Creativity',
      characterImg: '/assets/images/jessie.jpg',
      realImg: '/assets/images/Nabeel.png',
      tags: ['Social Media', 'Content Creation', 'Creative Strategy', 'Passionate Growth'],
      description: 'Like Jesse Pinkman, he brings raw creativity and passionate energy to everything he touches. Pure talent when it comes to crafting engaging content and building authentic brand connections. His innovative approach to social media strategy turns ordinary campaigns into viral gold. With undeniable charisma and street-smart intuition, he knows how to make brands explode in the digital space.',
      email: 'nabeel@quantumframestudio.com',
      quote: '"Yeah, we make content that\'s straight fire!"',
      indicator: { text: '🔥 CREATOR', color: 'rgba(255, 107, 53, 0.9)' },
      quoteColor: '#ff6b35',
      buttonGradient: 'linear-gradient(135deg, #ff6b35, #ff8c42)',
      buttonText: 'See the Magic'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100
      }
    }
  };

  const floatingAnimation = {
    y: [-5, 5, -5],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  return (
    <div className="abtu-about-us">
      <div className="abtu-particles-container">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="abtu-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      <ScrollProgressIndicator />

      <div className="abtu-video-container">
        <video 
          autoPlay 
          muted 
          loop 
          id="abtu-myVideo" 
          playsInline
          webkit-playsinline="true"
          preload="auto"
          className="abtu-background-video"
          style={{ pointerEvents: 'none' }}
        >
          <source src="/assets/video/About-us.mp4" type="video/mp4" />
        </video>
        <div className="abtu-video-overlay"></div>
      </div>

      <div className="abtu-content">
        {/* Hero Section */}
        <motion.section
          className="abtu-hero-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.5 }}
        >
          <motion.div 
            className="abtu-hero-content"
            animate={floatingAnimation}
          >
            <motion.h1 
              className="abtu-hero-heading"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5, duration: 1, type: "spring" }}
            >
              Our Empire
            </motion.h1>
            <motion.div 
              className="abtu-chemical-formula"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8 }}
            >
              C₈H₁₁NO₂ → Digital Solutions
            </motion.div>
            <motion.p 
              className="abtu-hero-subtitle"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
            >
              At QuantumFrame Studio, we cook up digital solutions that are 99.1% pure innovation. 
              We're not just in the business. We're building an empire of cutting-edge tech and unparalleled creativity.
            </motion.p>
          </motion.div>
        </motion.section>

        {/* Stats Section */}
        <motion.section
          className="abtu-stats-section"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div className="abtu-section-header" variants={itemVariants}>
            <h2 className="abtu-section-title">Lab Statistics</h2>
            <div className="abtu-section-divider"></div>
            <p className="abtu-section-subtitle">The numbers behind our operation</p>
          </motion.div>
          
          {!isMobile && (
            <div className="abtu-stats-grid">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className={`abtu-stat-item ${hoveredStat === index ? 'abtu-hovered' : ''}`}
                  variants={itemVariants}
                  whileHover={{ 
                    scale: 1.05,
                    rotateY: 5,
                    boxShadow: `0 20px 40px rgba(0, 0, 0, 0.3)`
                  }}
                  onHoverStart={() => setHoveredStat(index)}
                  onHoverEnd={() => setHoveredStat(null)}
                  style={{
                    '--stat-color': stat.color
                  }}
                >
                  <motion.div 
                    className="abtu-stat-icon"
                    animate={hoveredStat === index ? {
                      rotate: [0, 10, -10, 0],
                      scale: [1, 1.2, 1]
                    } : {}}
                    transition={{ duration: 0.5 }}
                  >
                    {stat.icon}
                  </motion.div>
                  <motion.div 
                    className="abtu-stat-number"
                    animate={hoveredStat === index ? {
                      textShadow: `0 0 20px ${stat.color}`
                    } : {}}
                  >
                    {stat.number}
                  </motion.div>
                  <div className="abtu-stat-label">{stat.label}</div>
                  <motion.div 
                    className="abtu-stat-glow"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={hoveredStat === index ? {
                      opacity: 0.6,
                      scale: 1.5
                    } : {
                      opacity: 0,
                      scale: 0
                    }}
                    style={{ backgroundColor: stat.color }}
                  />
                </motion.div>
              ))}
            </div>
          )}

          {isMobile && (
            <div className="abtu-stats-slider">
              <div className="abtu-stats-slider-container">
                <motion.div
                  key={currentStatIndex}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.5 }}
                  className="abtu-stat-slide"
                >
                  <motion.div 
                    className="abtu-stat-item-mobile"
                    style={{ '--stat-color': stats[currentStatIndex].color }}
                  >
                    <motion.div 
                      className="abtu-stat-icon"
                      animate={{
                        rotate: [0, 10, -10, 0],
                        scale: [1, 1.2, 1]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      {stats[currentStatIndex].icon}
                    </motion.div>
                    <motion.div 
                      className="abtu-stat-number"
                      animate={{
                        textShadow: `0 0 20px ${stats[currentStatIndex].color}`
                      }}
                    >
                      {stats[currentStatIndex].number}
                    </motion.div>
                    <div className="abtu-stat-label">{stats[currentStatIndex].label}</div>
                  </motion.div>
                </motion.div>
              </div>
              
              <div className="abtu-slider-indicators">
                {stats.map((_, index) => (
                  <div
                    key={index}
                    className={`abtu-slider-dot ${index === currentStatIndex ? 'active' : ''}`}
                    onClick={() => setCurrentStatIndex(index)}
                    style={{
                      backgroundColor: index === currentStatIndex ? stats[index].color : 'rgba(255, 255, 255, 0.3)'
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </motion.section>

        {/* Timeline Section */}
        <motion.section
          className="abtu-timeline-section"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div className="abtu-section-header" variants={itemVariants}>
            <h2 className="abtu-section-title">Our Journey</h2>
            <div className="abtu-section-divider"></div>
            <p className="abtu-section-subtitle">From the lab to the empire</p>
          </motion.div>

          <div className="abtu-timeline-container">
            {timeline.map((item, index) => (
              <motion.div
                key={index}
                className={`abtu-timeline-item ${hoveredTimeline === index ? 'abtu-hovered' : ''}`}
                variants={itemVariants}
                onHoverStart={() => setHoveredTimeline(index)}
                onHoverEnd={() => setHoveredTimeline(null)}
              >
                <motion.div 
                  className="abtu-timeline-icon"
                  animate={hoveredTimeline === index ? {
                    scale: [1, 1.2, 1],
                    rotate: [0, 360]
                  } : {}}
                  transition={{ duration: 0.6 }}
                  style={{ color: item.color }}
                >
                  {item.icon}
                </motion.div>
                <div className="abtu-timeline-content">
                  <motion.h3 
                    className="abtu-timeline-year"
                    style={{ color: item.color }}
                  >
                    {item.year}
                  </motion.h3>
                  <h4 className="abtu-timeline-title">{item.title}</h4>
                  <p className="abtu-timeline-description">{item.description}</p>
                </div>
                <motion.div 
                  className="abtu-timeline-line"
                  initial={{ height: 0 }}
                  whileInView={{ height: '100%' }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  style={{ background: `linear-gradient(180deg, ${item.color}, transparent)` }}
                />
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Services Section */}
        <motion.section
          className="abtu-services-section"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div className="abtu-section-header" variants={itemVariants}>
            <h2 className="abtu-section-title">What We Cook</h2>
            <div className="abtu-section-divider"></div>
            <p className="abtu-section-subtitle">Our specialized services</p>
          </motion.div>

          <div className="abtu-services-grid">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="abtu-service-card"
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.05,
                  y: -10
                }}
              >
                <motion.div 
                  className="abtu-service-icon"
                  style={{ color: service.color }}
                  whileHover={{
                    scale: 1.2,
                    rotate: 360
                  }}
                  transition={{ duration: 0.5 }}
                >
                  {service.icon}
                </motion.div>
                <h3 className="abtu-service-title">{service.title}</h3>
                <p className="abtu-service-description">{service.description}</p>
                <motion.div 
                  className="abtu-service-glow"
                  style={{ backgroundColor: service.color }}
                />
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Values Section */}
        <motion.section
          className="abtu-values-section"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div className="abtu-section-header" variants={itemVariants}>
            <h2 className="abtu-section-title">Our Formula</h2>
            <div className="abtu-section-divider"></div>
            <p className="abtu-section-subtitle">The key ingredients of our success</p>
          </motion.div>
          
          <div className={`abtu-values-grid ${isMobile ? 'abtu-values-grid-mobile' : ''}`}>
            {values.map((value, index) => (
              <motion.div
                key={index}
                className={`abtu-value-card ${hoveredValue === index ? 'abtu-hovered' : ''}`}
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.03,
                  rotateX: 5,
                  y: -10
                }}
                onHoverStart={() => setHoveredValue(index)}
                onHoverEnd={() => setHoveredValue(null)}
                style={{
                  background: hoveredValue === index ? value.gradient : 'rgba(255, 255, 255, 0.08)'
                }}
              >
                <motion.div 
                  className="abtu-value-icon"
                  animate={hoveredValue === index ? {
                    rotate: 360,
                    scale: [1, 1.3, 1]
                  } : {}}
                  transition={{ duration: 0.8 }}
                >
                  {value.icon}
                </motion.div>
                <h3 className="abtu-value-title">{value.title}</h3>
                <p className="abtu-value-description">{value.description}</p>
                <motion.div 
                  className="abtu-value-border"
                  initial={{ width: 0 }}
                  animate={hoveredValue === index ? { width: '100%' } : { width: 0 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Team Section */}
        <motion.section
          className="abtu-team-section"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <motion.div className="abtu-section-header" variants={itemVariants}>
            <h2 className="abtu-section-title">The Cooks</h2>
            <div className="abtu-section-divider"></div>
            <p className="abtu-section-subtitle">Meet the masterminds behind the operation</p>
          </motion.div>

          {team.map((member, index) => (
            <motion.div
              key={member.id}
              className={`abtu-team-member ${index % 2 === 1 ? 'abtu-reverse' : ''}`}
              variants={itemVariants}
            >
              <motion.div 
                className={`abtu-flip-card ${flippedCards[member.id] ? 'abtu-flipped' : ''}`}
                onClick={() => toggleCard(member.id)}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.5 }}
              >
                <div className="abtu-flip-card-inner">
                  <div className="abtu-flip-card-front">
                    <img src={member.characterImg} alt={member.name} className="abtu-founder-image" />
                    <div className="abtu-card-overlay">
                      <motion.div 
                        className="abtu-danger-indicator"
                        style={{ background: member.indicator.color }}
                        animate={{
                          opacity: [0.5, 1, 0.5],
                          scale: [1, 1.1, 1]
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity
                        }}
                      >
                        {member.indicator.text}
                      </motion.div>
                    </div>
                  </div>
                  <div className="abtu-flip-card-back">
                    <img src={member.realImg} alt={member.name} className="abtu-founder-image" />
                    <div className="abtu-back-content">
                      <motion.p 
                        className="abtu-founder-message"
                        animate={{
                          textShadow: [
                            `0 0 5px ${member.quoteColor}`,
                            `0 0 20px ${member.quoteColor}`,
                            `0 0 5px ${member.quoteColor}`
                          ]
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity
                        }}
                      >
                        {member.quote}
                      </motion.p>
                      <motion.a 
                        href="/contact" 
                        className="abtu-challenge-button"
                        style={{ background: member.buttonGradient }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <span>{member.buttonText}</span>
                      </motion.a>
                    </div>
                  </div>
                </div>
              </motion.div>
              
              <motion.div 
                className="abtu-text-container"
                variants={itemVariants}
              >
                <h2 className="abtu-team-member-name">{member.name}</h2>
                <h3 className="abtu-team-member-role">{member.role}</h3>
                <div className="abtu-expertise-tags">
                  {member.tags.map((tag, i) => (
                    <span key={i} className="abtu-tag">{tag}</span>
                  ))}
                </div>
                <p className="abtu-team-member-description">
                  {member.description}
                </p>
                <div className="abtu-contact-info">
                  <motion.a 
                    href={`mailto:${member.email}`}
                    className="abtu-contact-link"
                    whileHover={{
                      color: member.quoteColor,
                      textShadow: `0 0 8px ${member.quoteColor}`
                    }}
                  >
                    {member.email}
                  </motion.a>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.section>

        {/* CTA Section */}
        <motion.section
          className="abtu-ctab-section"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.div 
            className="abtu-ctab-content"
            animate={floatingAnimation}
          >
            <motion.h2 
              className="abtu-ctab-title"
              animate={{
                textShadow: [
                  '0 0 5px #00ff41',
                  '0 0 20px #00ff41',
                  '0 0 35px #00ff41',
                  '0 0 20px #00ff41',
                  '0 0 5px #00ff41'
                ]
              }}
              transition={{
                duration: 3,
                repeat: Infinity
              }}
            >
              Ready to Cook Something Great?
            </motion.h2>
            <p className="abtu-ctab-description">
              Let's discuss how we can help bring your digital vision to life with our 99.1% pure expertise and innovation.
            </p>
            <div className="abtu-ctab-buttons">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <a href="/contact" className="abtu-ctab-button abtu-primary">
                  <span>Start Cooking</span>
                  <div className="abtu-ctab-button-glow"></div>
                </a>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <a href="/testimonials" className="abtu-ctab-button abtu-secondary">
                  <span>See Our Product</span>
                </a>
              </motion.div>
            </div>
          </motion.div>
        </motion.section>
      </div>
    </div>
  );
};

export default AboutUs;